import { useEffect, useState, useCallback } from "react";
import BackToDashBar from "@/components/BackToDashBar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { CalendarClock, AlertTriangle, CheckCircle2, Clock, Plus, MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import RecordTrail from "@/components/RecordTrail";
import UserChip from "@/components/UserChip";

const CATEGORIES = ["tax_clearance", "payroll_tax", "vat", "corporate_tax", "license", "audit", "government_letter", "other"];
const FREQS = ["one_time", "monthly", "quarterly", "annual"];
const STATUSES = ["not_started", "in_progress", "submitted", "awaiting_response", "completed", "overdue"];

function dueChip(days) {
  if (days === null || days === undefined) return null;
  if (days < 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Overdue {Math.abs(days)}d</Badge>;
  if (days <= 7) return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Due in {days}d</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Due in {days}d</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{days}d left</Badge>;
}

function statusBadge(s) {
  const map = {
    not_started: "bg-slate-100 text-slate-700",
    in_progress: "bg-sky-100 text-sky-700",
    submitted: "bg-indigo-100 text-indigo-700",
    awaiting_response: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    overdue: "bg-red-100 text-red-700",
  };
  return <Badge className={`${map[s] || "bg-slate-100"} border-0`}>{s}</Badge>;
}

export default function CompliancePage() {
  const { user } = useAuth();
  const role = user?.role;
  const canCRUD = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_COMPLIANCE_OFFICER, ROLES.SALIHI_FINANCE_MANAGER].includes(role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [form, setForm] = useState({
    title: "", category: "tax_clearance", reference_no: "", authority: "",
    due_date: "", frequency: "one_time", assigned_to: "", notes: "",
  });
  const [followUpText, setFollowUpText] = useState("");
  const [users, setUsers] = useState([]);
  // P1 (2026-07-14) — Assign-user dialog from the table row "+ Assign" button
  // when a compliance item has no assignee yet.
  const [assignFor, setAssignFor] = useState(null);
  const [assignChoice, setAssignChoice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat) params.category = filterCat;
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get("/compliance", { params });
      setItems(data.items); setStats(data.stats);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filterCat, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canCRUD) api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, [canCRUD]);

  function resetForm() {
    setForm({ title: "", category: "tax_clearance", reference_no: "", authority: "",
              due_date: "", frequency: "one_time", assigned_to: "", notes: "" });
    setEditing(null);
  }

  async function save() {
    if (!form.title || !form.due_date) { toast.error("Title and due date are required"); return; }
    try {
      if (editing) {
        await api.patch(`/compliance/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/compliance", form);
        toast.success("Created");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function markComplete(id) {
    try { await api.post(`/compliance/${id}/complete`); toast.success("Marked completed"); load(); if (drawer?.id === id) setDrawer(null); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function assignUser(itemId, userId) {
    try {
      await api.patch(`/compliance/${itemId}`, { assigned_to: userId || null });
      toast.success(userId ? "Assigned" : "Cleared");
      setAssignFor(null); setAssignChoice(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function addFollowUp() {
    if (!followUpText.trim()) return;
    try {
      const { data } = await api.post(`/compliance/${drawer.id}/follow-up`, { note: followUpText });
      setDrawer(data); setFollowUpText(""); load();
      toast.success("Follow-up added");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-5" data-testid="compliance-page">
      <BackToDashBar />
      <div>
        <h2 className="text-2xl font-bold">Compliance Procedures</h2>
        <p className="text-sm text-slate-500 mt-1">Track deadlines, file submissions, and log follow-ups.</p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compliance-stats">
        <Card><CardContent className="p-4 flex items-center gap-3"><CalendarClock className="h-5 w-5 text-[#4FA3D1]" /><div><p className="text-xs uppercase text-slate-500">Total items</p><p className="text-xl font-bold">{stats.total ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-red-600" /><div><p className="text-xs uppercase text-slate-500">Overdue</p><p className="text-xl font-bold text-red-700">{stats.overdue ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Clock className="h-5 w-5 text-orange-600" /><div><p className="text-xs uppercase text-slate-500">Due in 7d</p><p className="text-xl font-bold text-orange-700">{stats.due_7 ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><div><p className="text-xs uppercase text-slate-500">Completed this month</p><p className="text-xl font-bold text-emerald-700">{stats.completed_this_month ?? 0}</p></div></CardContent></Card>
      </div>

      {/* Filters + create */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category" className="w-44"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-status" className="w-44"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:ml-auto">
          {canCRUD && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="new-compliance-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> New compliance item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} compliance item</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="comp-title" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="comp-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Frequency</Label>
                      <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                        <SelectTrigger data-testid="comp-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Authority</Label><Input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} /></div>
                    <div><Label>Reference no</Label><Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="comp-due-date" /></div>
                    <div><Label>Assign to</Label>
                      <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">(none)</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={save} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-compliance-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card><CardContent className="p-0">
        {loading
          ? <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
          : <div className="overflow-x-auto">
              <Table data-testid="compliance-table">
                <TableHeader><TableRow>
                  <TableHead>Title</TableHead><TableHead>Category</TableHead>
                  <TableHead>Authority</TableHead><TableHead>Due</TableHead>
                  <TableHead>Status</TableHead><TableHead>Assigned</TableHead>
                  <TableHead className="hidden md:table-cell">Owner</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {items.length === 0
                    ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No compliance items.</TableCell></TableRow>
                    : items.map((it) => (
                      <TableRow
                        key={it.id}
                        data-testid={`compliance-row-${it.id}`}
                        onClick={() => setDrawer(it)}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                      >
                        <TableCell><span className="text-[#4FA3D1] hover:underline text-left">{it.title}</span></TableCell>
                        <TableCell><Badge variant="secondary">{it.category}</Badge></TableCell>
                        <TableCell>{it.authority || "—"}</TableCell>
                        <TableCell><div className="flex flex-col gap-1"><span>{it.due_date}</span>{dueChip(it.days_remaining)}</div></TableCell>
                        <TableCell>{statusBadge(it.status)}</TableCell>
                        <TableCell data-testid={`assigned-cell-${it.id}`}>
                          <UserChip
                            user={it.assigned_user || null}
                            onAssign={canCRUD ? () => { setAssignFor(it); setAssignChoice(""); } : undefined}
                            testid={`assigned-chip-${it.id}`}
                          />
                        </TableCell>
                        <TableCell className="hidden md:table-cell" data-testid={`owner-cell-${it.id}`}>
                          <UserChip user={it.owner_user} showEmail={false} testid={`owner-chip-${it.id}`} />
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {canCRUD && it.status !== "completed" && (
                            <Button size="sm" variant="outline" onClick={() => markComplete(it.id)} data-testid={`complete-${it.id}`}>Complete</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
        }
      </CardContent></Card>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {drawer && <>
            <SheetHeader><SheetTitle>{drawer.title}</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Category</span><div><Badge variant="secondary">{drawer.category}</Badge></div></div>
                <div><span className="text-slate-500">Status</span><div>{statusBadge(drawer.status)}</div></div>
                <div><span className="text-slate-500">Authority</span><div>{drawer.authority || "—"}</div></div>
                <div><span className="text-slate-500">Reference</span><div>{drawer.reference_no || "—"}</div></div>
                <div><span className="text-slate-500">Due date</span><div>{drawer.due_date}</div></div>
                <div><span className="text-slate-500">Frequency</span><div>{drawer.frequency}</div></div>
                <div className="col-span-2"><span className="text-slate-500">Assigned</span><div className="mt-1"><UserChip user={drawer.assigned_user || null} onAssign={canCRUD ? () => { setAssignFor(drawer); setAssignChoice(""); } : undefined} testid="drawer-assigned-chip" /></div></div>
                <div className="col-span-2"><span className="text-slate-500">Owner</span><div className="mt-1"><UserChip user={drawer.owner_user} testid="drawer-owner-chip" /></div></div>
                {drawer.notes && <div className="col-span-2"><span className="text-slate-500">Notes</span><p className="mt-1 text-slate-700">{drawer.notes}</p></div>}
              </div>

              {canCRUD && drawer.status !== "completed" && (
                <div className="rounded-md border border-slate-200 p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 uppercase">Add follow-up</p>
                  <Textarea rows={3} value={followUpText} onChange={(e) => setFollowUpText(e.target.value)} placeholder="Notes on actions taken…" data-testid="follow-up-text" />
                  <Button size="sm" onClick={addFollowUp} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="add-follow-up-btn">
                    <MessageSquarePlus className="h-4 w-4 mr-1.5" /> Add follow-up
                  </Button>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Follow-up log</p>
                {(drawer.follow_up_log || []).length === 0
                  ? <p className="text-sm text-slate-400">No follow-ups yet.</p>
                  : <ul className="space-y-2">
                      {drawer.follow_up_log.map((f, i) => (
                        <li key={i} className="rounded-md bg-slate-50 p-3 text-sm">
                          <p className="text-xs text-slate-500 mb-1">{f.date?.slice(0, 19).replace("T", " ")} — {f.user_email}</p>
                          <p>{f.note}</p>
                        </li>
                      ))}
                    </ul>}
              </div>
              <RecordTrail entity_type="compliance" entity_id={drawer.id} record={drawer} />
            </div>
          </>}
        </SheetContent>
      </Sheet>

      <Dialog open={!!assignFor} onOpenChange={(v) => { if (!v) { setAssignFor(null); setAssignChoice(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign compliance item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{assignFor?.title}</p>
            <div>
              <Label>Assignee</Label>
              <Select value={assignChoice || "none"} onValueChange={(v) => setAssignChoice(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(Unassigned)</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignFor(null); setAssignChoice(""); }}>Cancel</Button>
            <Button
              onClick={() => assignUser(assignFor.id, assignChoice)}
              className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white"
              data-testid="confirm-assign-btn"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
